"use client"

import { useEffect } from 'react'
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Car, User, MessageCircle, LayoutDashboard, Bell, BellRing, Loader2 } from 'lucide-react'
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu"
import { Switch } from "@/components/ui/switch"
import { signOut } from 'next-auth/react'
import { useSession } from "next-auth/react";
import { useNotifications } from "@/hooks/useNotifications"
import { usePushSetup } from "@/hooks/usePushSetup"
import { showSystemNotification } from "@/lib/push-client"
import { useToast } from "@/components/ui/toast/use-toast"
import { ToastAction } from "@/components/ui/toast"
import { formatNotificationTime } from "@/lib/date-utils"
import { PushBanner } from "@/components/PushBanner"

export function Header({ isAdmin = false }: { isAdmin?: boolean }) {
  const { data: session } = useSession();
  const router = useRouter()
  const { toast } = useToast()
  const { unreadCount, chatUnreadCount, reportCount, notifications, arrived, fetchNotifications, markAsRead } = useNotifications()
  const { subscribed, loading: pushLoading, setPushEnabled } = usePushSetup()

  useEffect(() => {
    for (const n of arrived) {
      if (
        subscribed &&
        typeof document !== "undefined" &&
        document.hidden &&
        typeof Notification !== "undefined" &&
        Notification.permission === "granted"
      ) {
        showSystemNotification(n)
        continue
      }
      toast({
        title: n.title,
        description: n.description || undefined,
        ...(n.link
          ? {
              action: (
                <ToastAction
                  altText="Ver"
                  onClick={() => {
                    fetch("/api/notifications", {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ notificationIds: [n._id] }),
                    }).then(() => fetchNotifications())
                    setTimeout(() => router.push(n.link), 0)
                  }}
                >
                  Ver
                </ToastAction>
              ),
            }
          : {}),
      })
    }
  }, [arrived, fetchNotifications, router, toast, subscribed])

  const handleTogglePush = async (value: boolean) => {
    const ok = await setPushEnabled(value)
    if (ok) {
      toast({
        title: value ? "Notificações do sistema ativadas" : "Notificações do sistema desativadas",
      })
    } else {
      toast({
        variant: "destructive",
        title: "Não foi possível ativar",
        description: "O navegador bloqueou a permissão de notificações.",
      })
    }
  }

  const chatUnread = chatUnreadCount
  const reportUnread = reportCount

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white">
      <PushBanner />
      <div className="container relative flex h-16 items-center justify-between px-4">
        <Link
          href="/"
          className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center space-x-2 md:static md:translate-x-0 md:translate-y-0"
        >
          <Car className="h-6 w-6 text-blue-600" />
          <span className="text-xl font-bold text-blue-600">Pediloo</span>
        </Link>

        <nav className="hidden md:flex items-center space-x-6">
          <Link href="/search" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
            Buscar carona
          </Link>
          {session && (
            <>
              <Link href="/offer-seats" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
                Oferecer carona
              </Link>
              <Link href="/my-rides" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
                Minhas Caronas
              </Link>
            </>
          )}
          <Link href="/help" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
            Central de Ajuda
          </Link>
          <div className="flex items-center space-x-2">
            {session && (
              <>
              <Button variant="ghost" size="icon" className="relative text-gray-600 hover:text-blue-600" asChild>
                <Link href="/my-conversations">
                  <MessageCircle className="h-5 w-5" />
                  <span className="sr-only">Minhas Conversas</span>
                  {chatUnread > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 min-w-[16px] bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center px-1">
                      {chatUnread > 9 ? "9+" : chatUnread}
                    </span>
                  )}
                </Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative text-gray-600 hover:text-blue-600">
                    <Bell className="h-5 w-5" />
                    <span className="sr-only">Notificações</span>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 min-w-[16px] bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center px-1">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  {/* add a link inside notificões para a página de notificações */}
                  <DropdownMenuLabel className="flex justify-between items-center">
                    Notificações
                    <Link href="/notifications" className="text-xs text-blue-500 hover:text-blue-700">
                      Ver tudo
                    </Link>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.filter((n) => n.type !== "chat").length === 0 ? (
                      <div className="px-4 py-6 text-sm text-gray-500 text-center">
                        Nenhuma notificação
                      </div>
                    ) : (
                      notifications
                        .filter((n) => n.type !== "chat")
                        .map((n) => (
                        <DropdownMenuItem key={n._id} asChild>
                          <Link
                            href={n.link || "/trip"}
                            className="block w-full"
                            onClick={() => {
                              if (!n.read) {
                                fetch("/api/notifications", {
                                  method: "PATCH",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ notificationIds: [n._id] }),
                                }).then(() => fetchNotifications())
                              }
                            }}
                          >
                            <div className="flex items-start justify-between gap-2 w-full">
                              <div className="min-w-0">
                                <span className={n.read ? "font-normal" : "font-semibold"}>
                                  {n.title}
                                </span>
                                {n.description && (
                                  <span className="block text-xs text-gray-500 truncate">
                                    {n.description}
                                  </span>
                                )}
                              </div>
                              <span className="shrink-0 text-[11px] text-gray-400 whitespace-nowrap">
                                {formatNotificationTime(n.createdAt)}
                              </span>
                            </div>
                          </Link>
                        </DropdownMenuItem>
                      ))
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <label className="flex items-center justify-between px-4 py-3 cursor-pointer">
                    <span className="flex items-center gap-2 text-sm text-gray-600">
                      <BellRing className="h-4 w-4 text-blue-600" />
                      Notificações do sistema
                    </span>
                    {pushLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    ) : (
                      <Switch checked={subscribed} onCheckedChange={handleTogglePush} />
                    )}
                  </label>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-gray-600 hover:text-blue-600">
                  <User className="h-5 w-5" />
                  {/* <span className="sr-only">Menu do usuário</span> */}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!session ? (
                  <>
                    <DropdownMenuItem asChild>
                      <Link href="/login" className="text-gray-600 hover:text-blue-600">Entrar</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/register" className="text-gray-600 hover:text-blue-600">Cadastrar</Link>
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem asChild>
                      <Link href="/profile" className="text-gray-600 hover:text-blue-600">Perfil</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/my-alerts" className="text-gray-600 hover:text-blue-600">Meus alertas</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/my-reports" className="text-gray-600 hover:text-blue-600 relative" onClick={() => markAsRead(undefined, "report")}>
                        Minhas Denúncias
                        {reportUnread > 0 && (
                          <span className="ml-2 h-4 min-w-[16px] bg-red-500 rounded-full text-[10px] text-white inline-flex items-center justify-center px-1">
                            {reportUnread > 9 ? "9+" : reportUnread}
                          </span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {isAdmin && (
                      <>
                        <DropdownMenuLabel>Admin</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link href="/admin" className="flex items-center">
                            <LayoutDashboard className="mr-2 h-4 w-4" />
                            <span>Dashboard</span>
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Button variant="ghost" size="icon" className="text-gray-600 hover:text-blue-600 w-full px-4 py-2 text-left" onClick={() => signOut({ callbackUrl: "/" })}>Sair</Button>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </nav>

        <div className="ml-auto flex items-center md:hidden">
          {session && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative text-gray-600 hover:text-blue-600">
                <Bell className="h-5 w-5" />
                <span className="sr-only">Notificações</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-[16px] bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center px-1">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex justify-between items-center">
                Notificações
                <Link href="/notifications" className="text-xs text-blue-500 hover:text-blue-700">
                  Ver tudo
                </Link>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="max-h-96 overflow-y-auto">
                {notifications.filter((n) => n.type !== "chat").length === 0 ? (
                  <div className="px-4 py-6 text-sm text-gray-500 text-center">
                    Nenhuma notificação
                  </div>
                ) : (
                  notifications
                    .filter((n) => n.type !== "chat")
                    .map((n) => (
                    <DropdownMenuItem key={n._id} asChild>
                      <Link
                        href={n.link || "/trip"}
                        className="block w-full"
                        onClick={() => {
                          if (!n.read) {
                            fetch("/api/notifications", {
                              method: "PATCH",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ notificationIds: [n._id] }),
                            }).then(() => fetchNotifications())
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-2 w-full">
                          <div className="min-w-0">
                            <span className={n.read ? "font-normal" : "font-semibold"}>
                              {n.title}
                            </span>
                            {n.description && (
                              <span className="block text-xs text-gray-500 truncate">
                                {n.description}
                              </span>
                            )}
                          </div>
                          <span className="shrink-0 text-[11px] text-gray-400 whitespace-nowrap">
                            {formatNotificationTime(n.createdAt)}
                          </span>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  ))
                )}
              </div>
              <DropdownMenuSeparator />
              <label className="flex items-center justify-between px-4 py-3 cursor-pointer">
                <span className="flex items-center gap-2 text-sm text-gray-600">
                  <BellRing className="h-4 w-4 text-blue-600" />
                  Notificações do sistema
                </span>
                {pushLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                ) : (
                  <Switch checked={subscribed} onCheckedChange={handleTogglePush} />
                )}
              </label>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  )
}

